import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, ChevronLeft, Lock, CreditCard, MapPin } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { apiGet, apiPost, fmt, FREE_SHIPPING_THRESHOLD, SHIPPING_RATES } from '../lib/api';

const METHODS = [
  { id: 'standard', name: 'Standard Freight', desc: '3–5 business days', free: true },
  { id: 'expedited', name: 'Expedited Air', desc: 'Next business day', free: false },
  { id: 'freight', name: 'Lift-Gate Truck', desc: 'Heavy components, tailgate delivery', free: false },
];

export default function Checkout() {
  const nav = useNavigate();
  const { items, subtotal, discount, promo, clear } = useCart();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [cust, setCust] = useState({ name: '', email: '', phone: '', address: '', city: '', state: '', zip: '' });
  const [addresses, setAddresses] = useState([]);
  const [saveAddr, setSaveAddr] = useState(false);
  const [taxAmount, setTaxAmount] = useState(0);
  const [method, setMethod] = useState('standard');
  const [card, setCard] = useState({ number: '', exp: '', cvc: '' });
  const [placing, setPlacing] = useState(false);
  const [paying, setPaying] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (user) {
      setCust((c) => ({ ...c, name: c.name || user.name, email: c.email || user.email }));
      apiGet('/users/me/addresses').then(setAddresses).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (cust.state && subtotal > 0) {
      apiGet('/tax', { state: cust.state, subtotal: Math.max(subtotal - discount, 0) })
        .then((d) => setTaxAmount(d.tax))
        .catch(() => setTaxAmount(0));
    } else {
      setTaxAmount(0);
    }
  }, [cust.state, subtotal, discount]);

  const set = (k) => (e) => setCust({ ...cust, [k]: e.target.value });
  const applyAddr = (a) => setCust({ ...cust, name: a.name, phone: a.phone, address: a.address, city: a.city, state: a.state, zip: a.zip });

  const shipping = method === 'standard' && subtotal - discount >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_RATES[method];
  const total = subtotal - discount + shipping + taxAmount;
  const valid1 = cust.name && cust.email && cust.address && cust.city && cust.state && cust.zip;
  const valid3 = card.number.replace(/\s/g, '').length >= 12 && card.exp && card.cvc;

  const maybeSaveAddress = () => {
    if (user && saveAddr) {
      apiPost('/users/me/addresses', { label: cust.city || 'Farm', name: cust.name, phone: cust.phone, address: cust.address, city: cust.city, state: cust.state, zip: cust.zip })
        .then(() => apiGet('/users/me/addresses').then(setAddresses))
        .catch(() => {});
    }
  };

  const continueToDelivery = () => {
    maybeSaveAddress();
    setSaveAddr(false);
    setStep(2);
  };

  const payStripe = async () => {
    setPaying(true);
    setErr(null);
    try {
      const res = await apiPost('/payments/checkout', {
        items: items.map((i) => ({ product_id: i.id, qty: i.qty })),
        customer: cust,
        shipping_method: method,
        origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (ex) {
      setErr(ex.message);
      setPaying(false);
    }
  };

  const place = async () => {
    setPlacing(true);
    setErr(null);
    try {
      const res = await apiPost('/orders', {
        items: items.map((i) => ({ product_id: i.id, name: i.name, sku: i.sku, price: i.price, qty: i.qty, image: i.image })),
        customer: cust,
        shipping_method: method,
      });
      clear();
      nav(`/order/${res.order_number}`);
    } catch (ex) {
      setErr(ex.message);
      setPlacing(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="bg-cream min-h-screen grid place-items-center px-4 py-32 text-center">
        <div>
          <h1 className="font-display font-extrabold uppercase tracking-tight text-forest text-5xl">Cart's empty</h1>
          <p className="text-sub mt-3 mb-8">Add parts before heading to checkout.</p>
          <Link to="/shop" className="inline-block bg-flame hover:bg-ember text-white font-display font-semibold uppercase tracking-wide px-8 py-4 rounded-full transition-colors">Shop Parts</Link>
        </div>
      </div>
    );
  }

  const input = 'w-full bg-white border border-emerald-950/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-flame transition';
  const label = 'font-mono text-[10px] uppercase tracking-widest text-sub block mb-1.5';

  const step1 = (
    <>
      {user && addresses.length > 0 && (
        <div className="mb-6">
          <p className="font-mono text-[10px] uppercase tracking-widest text-sub mb-2 flex items-center gap-1.5"><MapPin className="w-3 h-3" /> Saved addresses</p>
          <div className="flex flex-wrap gap-2">
            {addresses.map((a) => (
              <button
                key={a.id}
                type="button"
                data-testid={`saved-address-${a.id}`}
                onClick={() => applyAddr(a)}
                className="text-left bg-white border-2 border-emerald-950/10 hover:border-flame rounded-xl px-4 py-2.5 text-sm transition"
              >
                <span className="font-semibold text-forest">{a.label}</span>
                <span className="text-sub"> · {a.city}, {a.state}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2"><label className={label}>Full name</label><input data-testid="checkout-name-input" className={input} value={cust.name} onChange={set('name')} placeholder="Sam Whitfield" /></div>
        <div><label className={label}>Email</label><input data-testid="checkout-email-input" type="email" className={input} value={cust.email} onChange={set('email')} placeholder="sam@farmmail.com" /></div>
        <div><label className={label}>Phone</label><input data-testid="checkout-phone-input" className={input} value={cust.phone} onChange={set('phone')} placeholder="(515) 555-0134" /></div>
        <div className="sm:col-span-2"><label className={label}>Farm / delivery address</label><input data-testid="checkout-address-input" className={input} value={cust.address} onChange={set('address')} placeholder="2840 County Road 142" /></div>
        <div><label className={label}>City</label><input data-testid="checkout-city-input" className={input} value={cust.city} onChange={set('city')} placeholder="Ames" /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className={label}>State</label><input data-testid="checkout-state-input" className={input} value={cust.state} onChange={set('state')} placeholder="IA" /></div>
          <div><label className={label}>ZIP</label><input data-testid="checkout-zip-input" className={input} value={cust.zip} onChange={set('zip')} placeholder="50010" /></div>
        </div>
        {user && (
          <label className="sm:col-span-2 flex items-center gap-2.5 text-sm text-ink cursor-pointer select-none">
            <input data-testid="save-address-checkbox" type="checkbox" checked={saveAddr} onChange={(e) => setSaveAddr(e.target.checked)} className="w-4 h-4 accent-[#FF5722]" />
            Save this address to my account for quicker checkout
          </label>
        )}
      </div>
    </>
  );

  const step2 = (
    <div className="space-y-3">
      {METHODS.map((m) => {
        const price = m.free && subtotal - discount >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_RATES[m.id];
        return (
          <label
            key={m.id}
            data-testid={`shipping-method-${m.id}`}
            className={`flex items-center gap-4 border-2 rounded-2xl p-5 cursor-pointer transition ${method === m.id ? 'border-flame bg-flame/5' : 'border-emerald-950/10 hover:border-emerald-950/25'}`}
          >
            <input type="radio" name="method" checked={method === m.id} onChange={() => setMethod(m.id)} className="w-4 h-4 accent-[#FF5722]" />
            <div className="flex-1">
              <p className="font-display font-semibold uppercase text-forest">{m.name}</p>
              <p className="text-xs text-sub">{m.desc}</p>
            </div>
            <span className="font-display font-bold text-xl text-forest">{price === 0 ? 'FREE' : fmt(price)}</span>
          </label>
        );
      })}
    </div>
  );

  const step3 = (
    <div className="space-y-4">
      <div className="bg-pine/10 border border-pine/20 rounded-2xl p-4 text-sm text-forest flex items-start gap-2.5">
        <Lock className="w-4 h-4 mt-0.5 text-ok shrink-0" />
        <p><span className="font-semibold">Secure checkout by Stripe.</span> Sales tax for {cust.state || 'your state'} is included in the total. Test card 4242 4242 4242 4242, any future expiry, any CVC.</p>
      </div>
      <button
        data-testid="stripe-checkout-button"
        disabled={paying}
        onClick={payStripe}
        className="w-full inline-flex justify-center items-center gap-2 bg-flame hover:bg-ember disabled:opacity-40 text-white font-display font-semibold uppercase tracking-wide px-8 py-4 rounded-full transition-colors"
      >
        <CreditCard className="w-5 h-5" /> {paying ? 'Connecting to Stripe…' : `Pay ${fmt(total)} with Stripe`}
      </button>

      <div className="flex items-center gap-3 py-1">
        <span className="flex-1 h-px bg-emerald-950/10" />
        <span className="font-mono text-[10px] uppercase tracking-widest text-sub">or demo order</span>
        <span className="flex-1 h-px bg-emerald-950/10" />
      </div>

      <div><label className={label}>Demo card number</label><input data-testid="checkout-card-number-input" className={input} value={card.number} onChange={(e) => setCard({ ...card, number: e.target.value.replace(/[^\d ]/g, '').slice(0, 19) })} placeholder="4242 4242 4242 4242" /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><label className={label}>Expiry</label><input data-testid="checkout-card-exp-input" className={input} value={card.exp} onChange={(e) => setCard({ ...card, exp: e.target.value.slice(0, 5) })} placeholder="12/28" /></div>
        <div><label className={label}>CVC</label><input data-testid="checkout-card-cvc-input" className={input} value={card.cvc} onChange={(e) => setCard({ ...card, cvc: e.target.value.replace(/\D/g, '').slice(0, 4) })} placeholder="123" /></div>
      </div>
      {err && <p data-testid="checkout-error-message" className="text-sm text-bad">{err}</p>}
      <button
        data-testid="checkout-submit-order-button"
        disabled={!valid3 || placing}
        onClick={place}
        className="w-full border-2 border-forest text-forest hover:bg-forest hover:text-cream disabled:opacity-40 font-display font-semibold uppercase tracking-wide px-8 py-4 rounded-full transition-colors"
      >
        {placing ? 'Placing demo order…' : `Place Demo Order — ${fmt(total)}`}
      </button>
    </div>
  );

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <Link to="/cart" className="inline-flex items-center gap-1.5 text-sm text-sub hover:text-flame transition mb-6"><ChevronLeft className="w-4 h-4" /> Back to cart</Link>
        <h1 className="font-display font-extrabold uppercase tracking-tight text-forest text-4xl sm:text-5xl mb-10">Checkout</h1>

        <div className="grid lg:grid-cols-[1fr_380px] gap-10 items-start">
          <div className="bg-white border border-emerald-950/10 rounded-3xl p-7 sm:p-9">
            <div className="flex items-center gap-2 mb-8">
              {['Shipping', 'Delivery', 'Payment'].map((t, i) => (
                <React.Fragment key={t}>
                  <div className="flex items-center gap-2">
                    <span className={`grid place-items-center w-8 h-8 rounded-full font-display font-bold ${step > i ? 'bg-flame text-white' : step === i + 1 ? 'bg-forest text-cream' : 'bg-silver text-sub'}`}>
                      {step > i + 1 ? <Check className="w-4 h-4" /> : i + 1}
                    </span>
                    <span className={`font-display font-semibold uppercase text-sm ${step >= i + 1 ? 'text-forest' : 'text-sub'}`}>{t}</span>
                  </div>
                  {i < 2 && <span className="flex-1 h-px bg-emerald-950/10 min-w-4" />}
                </React.Fragment>
              ))}
            </div>

            {step === 1 && (
              <>
                {step1}
                <button data-testid="checkout-continue-shipping-button" disabled={!valid1} onClick={continueToDelivery} className="mt-7 w-full bg-forest hover:bg-flame disabled:opacity-40 text-cream font-display font-semibold uppercase tracking-wide px-8 py-4 rounded-full transition-colors">
                  Continue to Delivery
                </button>
              </>
            )}
            {step === 2 && (
              <>
                {step2}
                <div className="flex gap-3 mt-7">
                  <button data-testid="checkout-back-delivery-button" onClick={() => setStep(1)} className="flex-1 border-2 border-forest text-forest hover:bg-forest hover:text-cream font-display font-semibold uppercase tracking-wide px-6 py-4 rounded-full transition-colors">Back</button>
                  <button data-testid="checkout-continue-payment-button" onClick={() => setStep(3)} className="flex-1 bg-forest hover:bg-flame text-cream font-display font-semibold uppercase tracking-wide px-6 py-4 rounded-full transition-colors">Continue to Payment</button>
                </div>
              </>
            )}
            {step === 3 && (
              <>
                {step3}
                <button data-testid="checkout-back-payment-button" onClick={() => setStep(2)} className="mt-4 w-full font-display font-semibold uppercase tracking-wide text-sub hover:text-flame transition py-2">← Back to delivery</button>
              </>
            )}
          </div>

          <aside className="lg:sticky lg:top-24 bg-forest text-cream rounded-3xl p-7 noise relative overflow-hidden">
            <h2 className="font-display font-bold uppercase text-2xl mb-5">Order Summary</h2>
            <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
              {items.map((i) => (
                <div key={i.id} className="flex gap-3 items-center">
                  <img src={i.image} alt={i.name} className="w-12 h-12 rounded-lg object-cover bg-white/10 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{i.name}</p>
                    <p className="font-mono text-[10px] text-cream/50 uppercase tracking-widest">×{i.qty} · {i.sku}</p>
                  </div>
                  <span className="font-semibold text-sm">{fmt(i.price * i.qty)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-white/15 mt-5 pt-4 space-y-2 text-sm">
              <div className="flex justify-between text-cream/70"><span>Subtotal</span><span data-testid="checkout-subtotal">{fmt(subtotal)}</span></div>
              {promo && <div className="flex justify-between text-ok"><span>Promo {promo.code}</span><span data-testid="checkout-discount">−{fmt(discount)}</span></div>}
              <div className="flex justify-between text-cream/70"><span>Shipping</span><span data-testid="checkout-shipping">{shipping === 0 ? 'FREE' : fmt(shipping)}</span></div>
              <div className="flex justify-between text-cream/70"><span>Sales tax {cust.state ? `(${cust.state.toUpperCase()})` : ''}</span><span data-testid="checkout-tax">{fmt(taxAmount)}</span></div>
              <div className="flex justify-between items-end border-t border-white/15 pt-3 mt-2">
                <span className="font-semibold">Total</span>
                <span data-testid="checkout-total" className="font-display font-bold text-3xl">{fmt(total)}</span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
